/**
 * PublishingCard — right-rail summary card showing the at-a-glance
 * publish state of the session. Mirrors `.publish-card` from the
 * prototype (cms-redesign-mockup.html ~8631–8653 + CSS lines
 * 3038–3075).
 *
 * Fields (all read-only):
 *   • Access — Free/Premium pill (from `session_is_free`)
 *   • Length — formatted `session_length` + raw seconds
 *   • Created — `session_created_at` (humanized)
 *   • Last updated — `session_updated_at` (short date + time)
 *   • Captions (full-width) — language chips for every caption row
 *     attached to this session, tinted by status (reviewed = teal,
 *     pending = sand, processing = blue, failed = coral). When
 *     `session_skip_captions` is on we render a single "Skipped"
 *     chip + footnote so the reason is explicit rather than the
 *     row just being empty.
 *
 * Plus a coral "Deactivate session" link in the footer that flips
 * `session_is_active` to 0 via the same setter the in-form Settings
 * uses — this is the prototype's primary destructive affordance
 * for the rail.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import TierPill from '../../redesign/TierPill';
import { LangChip } from '../../Captions/v2/captionsPrimitives';
import { apiV2 } from '../../../actions';

function formatLong(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatShortDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/* Status precedence for ordering the language chips: "needs human
 * attention" first (failed → pending), then in-flight, then settled,
 * then queued/unknown. Within a bucket we keep the English row
 * first (it's the source for everything else) and the rest
 * alphabetical by language code. */
const STATUS_RANK = {
  failed:              0,
  auto_pending_review: 1,
  pending:             1,
  processing:          2,
  human_edited:        3,
  human_reviewed:      3,
  edited:              3,
  reviewed:            3,
  queued:              4,
};

/* Human-readable status copy for the link `title` tooltip. Keeps the
 * inline hover affordance descriptive — "Open ES captions — Pending
 * review" reads better than "Open ES captions — auto_pending_review". */
const STATUS_LABEL_TEXT = {
  failed:              'Failed',
  auto_pending_review: 'Pending review',
  pending:             'Pending review',
  processing:          'Processing',
  human_edited:        'Edited',
  human_reviewed:      'Reviewed',
  edited:              'Edited',
  reviewed:            'Reviewed',
  queued:              'Queued',
};

function sortCaptions(rows) {
  return [...rows].sort((a, b) => {
    const ra = STATUS_RANK[(a.status || '').toLowerCase()] ?? 99;
    const rb = STATUS_RANK[(b.status || '').toLowerCase()] ?? 99;
    if (ra !== rb) return ra - rb;
    const aEn = (a.language_code || '').toLowerCase() === 'en' ? 0 : 1;
    const bEn = (b.language_code || '').toLowerCase() === 'en' ? 0 : 1;
    if (aEn !== bEn) return aEn - bEn;
    return String(a.language_code || '').localeCompare(b.language_code || '');
  });
}

/* Hook: load captions for a session, gated on edit-mode + skip flag. */
function useSessionCaptions(sessionId, skipCaptions) {
  const [state, setState] = useState({ status: 'idle', rows: [] });
  useEffect(() => {
    if (!sessionId || skipCaptions) {
      setState({ status: 'idle', rows: [] });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading' }));
    apiV2
      .get(`/admin/sessions/${sessionId}/captions`)
      .then((resp) => {
        if (cancelled) return;
        const rows = Array.isArray(resp?.data?.objects) ? resp.data.objects : [];
        setState({ status: 'loaded', rows });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', rows: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, skipCaptions]);
  return state;
}

export default function PublishingCard({ formData, onToggleActive }) {
  const isActive = !!formData.session_is_active;
  const isFree = !!formData.session_is_free;
  const skipCaptions = !!formData.session_skip_captions;
  const lengthSec = Number(formData.session_length_in_sec) || 0;
  const lengthLabel = formData.session_length
    ? `${formData.session_length}${lengthSec ? ` (${lengthSec} sec)` : ''}`
    : '—';

  const { status: capStatus, rows: capRows } = useSessionCaptions(
    formData.session_id,
    skipCaptions,
  );
  const orderedCaptions = sortCaptions(capRows);
  const translationCount = orderedCaptions.filter(
    (r) => (r.language_code || '').toLowerCase() !== 'en',
  ).length;

  return (
    <RailCard
      variant="publish"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Publishing"
          variant="publish"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-condensed font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '3px 9px',
                borderRadius: 999,
                background: isActive ? '#75A29333' /* teal-soft */ : '#9CA1A81F',
                color: isActive ? '#5C7E72' /* teal-d */ : '#9CA1A8',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '14px 18px' }}>
        <dl
          className="meta"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px 14px',
            margin: 0,
          }}
        >
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Access
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              <TierPill isFree={isFree} />
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Length
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {lengthLabel}
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Created
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {formatLong(formData.session_created_at)}
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Last updated
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {formatShortDateTime(formData.session_updated_at)}
            </dd>
          </div>

          {/* Captions — full-width row at the bottom. Shows a chip
              per language tinted by status, or a single "Skipped"
              chip when the skip-captions flag is on. */}
          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{
                fontSize: 10,
                letterSpacing: '0.08em',
                margin: 0,
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
              }}
            >
              <span>Captions</span>
              {!skipCaptions && capStatus === 'loaded' && orderedCaptions.length > 0 && (
                <span
                  className="font-mono"
                  style={{
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontWeight: 500,
                    color: '#9CA1A8',
                    fontSize: 10.5,
                  }}
                >
                  · {orderedCaptions.length} lang{orderedCaptions.length === 1 ? '' : 's'}
                  {translationCount > 0
                    ? ` (${translationCount} translation${translationCount === 1 ? '' : 's'})`
                    : ''}
                </span>
              )}
            </dt>
            <dd style={{ margin: '6px 0 0' }}>
              {skipCaptions ? (
                <div
                  className="flex items-center"
                  style={{ gap: 8, flexWrap: 'wrap' }}
                >
                  <span
                    className="font-condensed font-bold uppercase"
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.08em',
                      padding: '3px 9px',
                      borderRadius: 999,
                      background: '#9CA1A81F',
                      color: '#9CA1A8',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Skipped
                  </span>
                  <span
                    className="text-text-muted"
                    style={{ fontSize: 11, fontStyle: 'italic' }}
                  >
                    Pipeline disabled for this session
                  </span>
                </div>
              ) : capStatus === 'loading' ? (
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Loading…
                </span>
              ) : capStatus === 'error' ? (
                <span
                  className="text-coral-d"
                  style={{ fontSize: 11, fontStyle: 'italic' }}
                >
                  Couldn’t load captions
                </span>
              ) : orderedCaptions.length === 0 ? (
                <span
                  className="text-text-muted"
                  style={{ fontSize: 11, fontStyle: 'italic' }}
                >
                  None yet — will generate after audio uploads
                </span>
              ) : (
                <div
                  className="flex flex-wrap"
                  style={{ gap: 6 }}
                >
                  {orderedCaptions.map((c) => {
                    const label = STATUS_LABEL_TEXT[(c.status || '').toLowerCase()] || c.status || '';
                    const langCode = (c.language_code || '').toLowerCase();
                    const sourceParam = c.source ? `?source=${encodeURIComponent(c.source)}` : '';
                    return (
                      <Link
                        key={c.session_caption_id || `${c.language_code}-${c.status}`}
                        to={`/captions/edit/${formData.session_id}/${langCode}${sourceParam}`}
                        className="no-underline cursor-pointer"
                        title={`Open ${(c.language_code || '??').toUpperCase()} captions — ${label}`}
                        style={{
                          display: 'inline-flex',
                          textDecoration: 'none',
                          borderRadius: 4,
                          transition: 'transform 140ms ease, box-shadow 140ms ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 2px 6px #2C3E5018';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <LangChip code={c.language_code} status={c.status} />
                      </Link>
                    );
                  })}
                </div>
              )}
            </dd>
          </div>
        </dl>

        {/* Activate / Deactivate toggle link. Wires through the same
            setter as the in-form Settings card so both surfaces stay
            in sync. */}
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #ECE3D2', /* line */
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => onToggleActive?.(!isActive)}
            className="font-semibold cursor-pointer"
            style={{
              fontSize: 12,
              color: isActive ? '#C4715B' /* coral-d */ : '#5C7E72' /* teal-d */,
              background: 'transparent',
              border: 0,
              padding: 0,
              textDecoration: 'none',
            }}
          >
            {isActive ? 'Deactivate session' : 'Activate session'}
          </button>
        </div>
      </div>
    </RailCard>
  );
}
